import React, { useState } from 'react';
import {
  ArrowRight, CheckCircle, Phone, ShieldCheck, Sparkles, Loader2,
  BadgeCheck, Clock, MessageCircleHeart,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE = '0478 453 548';

const QUESTIONS = [
  { key: 'ownership', label: 'Do you own or rent your home?', options: ['Owner', 'Renter'] },
  { key: 'currentSystem', label: 'What hot water system do you currently have?', options: ['Electric storage', 'Gas', 'Solar', 'Heat pump', 'Not sure'] },
  { key: 'systemAge', label: 'Roughly how old is it?', options: ['0–5 yrs', '5–10 yrs', '10+ yrs', 'Not sure'] },
  { key: 'householdSize', label: 'How many people in your household?', options: ['1–2', '3–4', '5+'] },
];

const PillGroup = ({ label, options, value, onChange, testid }) => (
  <div data-testid={testid}>
    <Label className="text-sm font-bold text-[#3D5A47]">{label}</Label>
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
            value === opt
              ? 'bg-[#3D5A47] text-white border-[#3D5A47] shadow-sm'
              : 'bg-[#f3f6ee] text-gray-500 border-transparent hover:text-[#3D5A47] hover:bg-[#eaf2dc]'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const HeatPumpUpgradePage = () => {
  const [step, setStep] = useState('form'); // form | otp | done
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [form, setForm] = useState({
    name: '', mobile: '', email: '', address: '',
    ownership: '', currentSystem: '', systemAge: '', householdSize: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateForm = () => {
    if (!form.name.trim()) return 'Please enter your name';
    if (!/^[0-9 +()-]{8,}$/.test(form.mobile.trim())) return 'Please enter a valid mobile number';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return 'Please enter a valid email address';
    if (!form.address.trim()) return 'Please enter your address';
    for (const q of QUESTIONS) {
      if (!form[q.key]) return `Please answer: ${q.label}`;
    }
    return null;
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    const err = validateForm();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/heat-pump-upgrade/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Code sent! Check your phone 📲');
        setStep('otp');
      } else {
        toast.error(data.detail || 'Could not send the code. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.trim().length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/heat-pump-upgrade/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, otp: otp.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.verified) {
        setStep('done');
      } else {
        toast.error(data.detail || 'That code didn\u2019t match. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/heat-pump-upgrade/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) toast.success('New code sent 📲');
      else toast.error(data.detail || 'Could not resend the code.');
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
      <SEO
        title="Heat Pump Hot Water Upgrade — Check Your Government Rebate Eligibility"
        description="Upgrade to an energy-efficient heat pump hot water system. You could be eligible for generous Government rebates & incentives — pay little to nothing out of pocket. Enter your details to get started."
      />

      {/* MINIMAL TOP BAR — logo only, no site navigation */}

      <main className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-10 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
          {/* Headline hook — kept very short */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-black text-[#3D5A47] leading-tight tracking-tight" data-testid="hp-hook">
              You could qualify for the <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">Rebate!</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-2.5">It only takes 30 seconds.</p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-8" data-testid="hp-card">
            {step === 'form' && (
              <form onSubmit={handleSendOtp} className="space-y-5" data-testid="hp-form">
                <p className="text-sm font-semibold text-[#3D5A47]">Please enter your details below — no obligation.</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hp-name" className="text-sm font-bold text-[#3D5A47]">Full Name*</Label>
                    <Input id="hp-name" data-testid="hp-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Smith" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
                  </div>
                  <div>
                    <Label htmlFor="hp-mobile" className="text-sm font-bold text-[#3D5A47]">Mobile Number*</Label>
                    <Input id="hp-mobile" data-testid="hp-mobile" type="tel" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} placeholder="0412 345 678" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
                  </div>
                  <div>
                    <Label htmlFor="hp-email" className="text-sm font-bold text-[#3D5A47]">Email Address*</Label>
                    <Input id="hp-email" data-testid="hp-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@email.com" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
                  </div>
                  <div>
                    <Label htmlFor="hp-address" className="text-sm font-bold text-[#3D5A47]">Home Address*</Label>
                    <Input id="hp-address" data-testid="hp-address" value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="1 Example St, Suburb VIC" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
                  </div>
                </div>

                <div className="space-y-5 pt-2 border-t border-gray-100">
                  {QUESTIONS.map((q) => (
                    <PillGroup
                      key={q.key}
                      label={q.label}
                      options={q.options}
                      value={form[q.key]}
                      onChange={(v) => set(q.key, v)}
                      testid={`hp-${q.key}`}
                    />
                  ))}
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-base font-semibold py-6 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid="hp-submit">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Check My Eligibility <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <p className="text-[11px] text-gray-400 text-center">We&rsquo;ll send a quick code to your mobile to verify it&rsquo;s really you.</p>
              </form>
            )}

            {step === 'otp' && (
              <div className="space-y-5 text-center" data-testid="hp-otp">
                <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mx-auto">
                  <Sparkles className="w-7 h-7 text-[#3D5A47]" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-[#3D5A47]">Quick check — is it really you? 😊</h2>
                  <p className="text-sm text-gray-500 mt-1.5">
                    We&rsquo;ve texted a 6-digit code to <strong className="text-[#3D5A47]">{form.mobile}</strong>. Pop it in below and you&rsquo;re all done!
                  </p>
                </div>
                <Input
                  data-testid="hp-otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  placeholder="• • • • • •"
                  className="text-center text-2xl tracking-[0.5em] font-bold py-6 max-w-xs mx-auto"
                />
                <Button onClick={handleVerify} disabled={loading} className="w-full bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-base font-semibold py-6 rounded-xl" data-testid="hp-verify-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><BadgeCheck className="w-5 h-5" /> Verify &amp; Finish</>}
                </Button>
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <button type="button" onClick={handleResend} disabled={loading} className="hover:text-[#3D5A47] underline" data-testid="hp-resend">Resend code</button>
                  <button type="button" onClick={() => setStep('form')} className="hover:text-[#3D5A47] underline" data-testid="hp-edit-details">Edit my details</button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="text-center py-6" data-testid="hp-done">
                <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-5">
                  <MessageCircleHeart className="w-8 h-8 text-[#3D5A47]" />
                </div>
                <h2 className="text-2xl font-black text-[#3D5A47]">Thanks, {form.name.split(' ')[0] || 'there'}! 🎉</h2>
                <p className="text-gray-600 mt-3 max-w-md mx-auto leading-relaxed">
                  We&rsquo;ve received your details and our friendly team will be in touch shortly to confirm your
                  rebate eligibility and the next steps for your heat pump upgrade.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 bg-[#f8faf5] border border-gray-100 rounded-full px-4 py-2">
                  <Clock className="w-4 h-4 text-[#3D5A47]" /> Most enquiries answered within one business day
                </div>
                <div className="mt-7">
                  <a href={`tel:${PHONE.replace(/\s/g, '')}`}>
                    <Button className="bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 px-6 py-5 rounded-xl"><Phone className="w-4 h-4" /> Call us now</Button>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Trust strip */}
          {step === 'form' && (
            <div className="mt-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="hp-trust">
                {[
                  'Slash your hot water running costs',
                  'Rebates & incentives applied for you',
                  'CEC-accredited, fully installed',
                ].map((b) => (
                  <div key={b} className="flex items-center gap-2.5 text-sm text-gray-700 bg-white/70 rounded-xl px-4 py-3 border border-gray-100">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" /> {b}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* MINIMAL FOOTER */}
      <footer className="border-t border-gray-100 bg-white/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#3D5A47]" /> Your details stay private &amp; secure</span>
          <span>© {new Date().getFullYear()} Supa Energy Hub · <a href="/privacy" className="underline hover:text-[#3D5A47]">Privacy Policy</a></span>
        </div>
      </footer>
    </div>
  );
};

export default HeatPumpUpgradePage;
